"use client"

import { useEffect } from "react"

import { Button } from "@/components/ui/button"

/**
 * Global Error Boundary
 *
 * This error boundary catches errors in the application and displays
 * a user-friendly error message with recovery options.
 *
 * Features:
 * - User-friendly error message
 * - Reset button to retry the operation
 * - Go home button as an escape hatch
 * - Error digest (ID) for support and debugging
 * - Ready for error monitoring integration (Sentry, LogRocket, etc.)
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    // Log error to console in development
    console.error("Application error:", error)

    // TODO: Send to error monitoring service
    // Example with Sentry:
    // Sentry.captureException(error)
    //
    // Example with LogRocket:
    // LogRocket.captureException(error)
  }, [error])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-foreground mb-4 text-4xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground mb-8">
          We encountered an unexpected error. Please try again or contact support if the problem persists.
        </p>
        {error.digest && <p className="text-muted-foreground mb-4 font-mono text-sm">Error ID: {error.digest}</p>}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <Button onClick={reset} size="lg">
            Try Again
          </Button>
          <Button variant="outline" size="lg" onClick={() => (window.location.href = "/")}>
            Go Home
          </Button>
        </div>
      </div>
    </div>
  )
}
